import { FC, useRef, useState, useCallback, useEffect } from 'react'

import PlayIcon from 'src/Assets/Svg/video-play.svg?react'
import PauseIcon from 'src/Assets/Svg/video-pause.svg?react'
import LoadingIcon from 'src/Assets/Svg/video-loading.svg?react'
import EyeCrossedIcon from 'src/Assets/Svg/eye-crossed.svg?react'

import {
  Container,
  Overlay,
  PlayButton,
  Video,
  NotAvailableContainer,
  NotAvailableText,
  IconWrapper,
  SpinningIconWrapper,
} from './styles'

export interface VideoPlayerProps {
  videoUrl?: string
  posterUrl?: string
  className?: string
  paused?: boolean
  onPlay?: () => void
}

export const VideoPlayer: FC<VideoPlayerProps> = ({
  videoUrl,
  posterUrl,
  className,
  paused,
  onPlay,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [prevPaused, setPrevPaused] = useState(paused)

  if (prevPaused !== paused) {
    setPrevPaused(paused)
    if (paused && isPlaying) {
      setIsPlaying(false)
    }
  }

  useEffect(() => {
    if (paused && videoRef.current) {
      videoRef.current.pause()
    }
  }, [paused])

  const handlePlay = useCallback(() => {
    if (!videoRef.current) {
      return
    }

    setIsLoading(true)
    onPlay?.()
    videoRef.current
      .play()
      .then(() => {
        setIsPlaying(true)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [onPlay])

  const handlePause = useCallback(() => {
    if (!videoRef.current) {
      return
    }

    videoRef.current.pause()
    setIsPlaying(false)
  }, [])

  const handleToggle = useCallback(() => {
    if (isPlaying) {
      handlePause()
    } else {
      handlePlay()
    }
  }, [isPlaying, handlePlay, handlePause])

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false)
  }, [])

  const handleVideoWaiting = useCallback(() => {
    setIsLoading(true)
  }, [])

  const handleVideoPlaying = useCallback(() => {
    setIsLoading(false)
  }, [])

  const renderPlayButtonContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <SpinningIconWrapper>
          <LoadingIcon />
        </SpinningIconWrapper>
      )
    }

    if (isPlaying) {
      return <PauseIcon />
    }

    return <PlayIcon />
  }

  if (!videoUrl) {
    return (
      <NotAvailableContainer className={className}>
        <IconWrapper>
          <EyeCrossedIcon />
        </IconWrapper>
        <NotAvailableText>
          <p>Oops!</p>
          <p>Preview is not available</p>
        </NotAvailableText>
      </NotAvailableContainer>
    )
  }

  return (
    <Container className={className}>
      <Video
        ref={videoRef}
        src={videoUrl}
        poster={posterUrl}
        onEnded={handleVideoEnded}
        onWaiting={handleVideoWaiting}
        onPlaying={handleVideoPlaying}
        playsInline
        preload='metadata'
      />
      <Overlay />
      <PlayButton onClick={handleToggle}>
        {renderPlayButtonContent()}
      </PlayButton>
    </Container>
  )
}
